<template>
  <div class="strip-question-editor p-1">
    <!-- Parts list (draggable, only non-hint parts) -->
    <h6 class="mb-1">Части вопроса</h6>
    <div v-if="!contentParts.length && !hintParts.length" class="text-muted small mb-1">Нет частей</div>

    <draggable
      v-model="contentParts"
      handle=".part-drag-handle"
      :options="{ animation: 200 }"
      @end="onPartsReorder"
    >
      <div
        v-for="(part, idx) in contentParts"
        :key="part.id"
        class="part-row d-flex mb-1"
        :class="{ 'has-hints': isLastContentIdx(idx) && hintParts.length }"
      >
        <b-card no-body class="part-card flex-grow-1 mb-0">
        <div class="p-1">
          <div class="d-flex align-items-center mb-1">
            <feather-icon icon="GridIcon" class="part-drag-handle cursor-move mr-1 text-muted" />
            <b-badge variant="light-secondary" class="mr-1">#{{ part.order }}</b-badge>
            <b-badge :variant="typeVariant(part.type)" class="mr-1">{{ typeLabel(part.type) }}</b-badge>
            <small class="text-muted">id={{ part.id }}</small>
            <b-button
              size="sm"
              variant="link"
              class="text-danger ml-auto p-0"
              :disabled="deletingPartId === part.id"
              :title="'Удалить часть #' + part.order"
              @click="deletePart(part)"
            >
              <b-spinner v-if="deletingPartId === part.id" small />
              <feather-icon v-else icon="Trash2Icon" />
            </b-button>
          </div>

          <!-- text part -->
          <div v-if="part.type === 'text'">
            <b-input-group>
              <b-form-textarea v-model="textBuffers[part.id]" rows="2" />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="textBuffers[part.id] === part.text || savingPartId === part.id"
                  @click="saveTextPart(part)"
                >
                  <b-spinner v-if="savingPartId === part.id" small /> Сохранить
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <!-- audio part -->
          <div v-else-if="part.type === 'audio'">
            <audio v-if="part.url" :src="part.url" controls preload="metadata" class="w-100 mb-1" />
            <b-button
              v-if="!audioReplaceActive[part.id]"
              size="sm"
              variant="outline-primary"
              @click="openAudioReplace(part)"
            >
              <feather-icon icon="EditIcon" /> Перерезать / заменить трек
            </b-button>

            <div v-else class="audio-replace-panel border rounded p-1 mt-1">
              <b-form-group label="Артист" class="mb-1">
                <v-select
                  v-model="audioReplaceState[part.id].artist"
                  :options="artistOptions"
                  :filterable="false"
                  label="title"
                  placeholder="Поиск артиста"
                  @search="onArtistSearch"
                  @input="onArtistChanged(part)"
                />
              </b-form-group>

              <div v-if="audioReplaceState[part.id].artist">
                <b-spinner v-if="audioReplaceState[part.id].loadingTracks" small />
                <b-list-group v-else class="track-list mb-1">
                  <b-list-group-item
                    v-for="t in audioReplaceState[part.id].tracks"
                    :key="t.id"
                    :active="audioReplaceState[part.id].track && audioReplaceState[part.id].track.id === t.id"
                    class="cursor-pointer"
                    @click="selectReplacementTrack(part, t)"
                  >
                    <div class="d-flex justify-content-between">
                      <span><strong>{{ t.artist }}</strong> — {{ t.track }}</span>
                      <small class="text-muted">{{ t.play_count || 0 }} прослушиваний</small>
                    </div>
                  </b-list-group-item>
                </b-list-group>

                <strip-audio-cutter
                  v-if="audioReplaceState[part.id].track"
                  v-model="audioReplaceState[part.id].cut"
                  :audio-url="audioReplaceState[part.id].track.url"
                  :default-start="55"
                  :default-duration="25"
                />
              </div>

              <div class="d-flex mt-1" style="gap: 8px">
                <b-button size="sm" variant="primary" :disabled="!canSubmitReplace(part) || savingPartId === part.id" @click="submitAudioReplace(part)">
                  <b-spinner v-if="savingPartId === part.id" small /> Применить
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="closeAudioReplace(part)">
                  Отмена
                </b-button>
              </div>
            </div>
          </div>

          <!-- image part -->
          <div v-else-if="part.type === 'image'">
            <div v-if="part.url" class="mb-1">
              <img :src="part.url" alt="" class="part-thumb" />
            </div>
          </div>

          <!-- video / other -->
          <div v-else>
            <small class="text-muted">Тип <code>{{ part.type }}</code> пока редактируется только удалением</small>
          </div>
        </div>
        </b-card>

        <!-- Hint slot — appears only on last content row -->
        <div v-if="isLastContentIdx(idx) && hintParts.length" class="hint-slot ml-1">
          <div v-for="hp in hintParts" :key="hp.id" class="hint-item mb-1">
            <b-input-group size="sm">
              <b-form-input
                v-model="textBuffers[hp.id]"
                class="hint-input"
                :placeholder="'#' + hp.order"
              />
              <b-input-group-append>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="textBuffers[hp.id] === hp.text || savingPartId === hp.id"
                  @click="saveTextPart(hp)"
                >
                  <b-spinner v-if="savingPartId === hp.id" small />
                  <feather-icon v-else icon="CheckIcon" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
      </div>
    </draggable>

    <!-- Hint parts (fallback row when there are no content parts) -->
    <div v-if="hintParts.length && !contentParts.length" class="hints-row d-flex flex-wrap align-items-center mb-1" style="gap: 6px">
      <feather-icon icon="MessageSquareIcon" class="text-muted" />
      <small class="text-muted mr-1">Подсказки:</small>
      <div v-for="part in hintParts" :key="part.id" class="hint-item">
        <b-input-group size="sm">
          <b-form-input
            v-model="textBuffers[part.id]"
            class="hint-input"
            :placeholder="'#' + part.order"
          />
          <b-input-group-append>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="textBuffers[part.id] === part.text || savingPartId === part.id"
              @click="saveTextPart(part)"
            >
              <b-spinner v-if="savingPartId === part.id" small />
              <feather-icon v-else icon="CheckIcon" />
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="deletingPartId === part.id"
              :title="'Удалить подсказку'"
              @click="deletePart(part)"
            >
              <b-spinner v-if="deletingPartId === part.id" small />
              <feather-icon v-else icon="XIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </div>

    <!-- Add new part -->
    <div class="add-part-block mt-1 mb-1">
      <div v-if="!addPartMode" class="d-flex" style="gap: 6px">
        <small class="text-muted align-self-center mr-1">Добавить:</small>
        <b-button size="sm" variant="outline-secondary" @click="openAddPart('text')">
          <feather-icon icon="TypeIcon" /> Текст
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="openAddPart('hint')">
          <feather-icon icon="MessageSquareIcon" /> Подсказка
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="openAddPart('audio')">
          <feather-icon icon="MusicIcon" /> Аудио
        </b-button>
      </div>

      <div v-else class="border rounded p-1 add-part-form">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong>
            Новая часть:
            <span v-if="addPartMode === 'text'">текст</span>
            <span v-else-if="addPartMode === 'hint'">подсказка</span>
            <span v-else>аудио</span>
          </strong>
          <b-button size="sm" variant="link" @click="closeAddPart">Отмена</b-button>
        </div>

        <div v-if="addPartMode === 'text' || addPartMode === 'hint'">
          <b-form-textarea
            v-model="newPartText"
            :rows="addPartMode === 'hint' ? 1 : 2"
            :placeholder="addPartMode === 'hint' ? 'Например: АП' : 'Текст подсказки/комментария'"
          />
        </div>

        <div v-else-if="addPartMode === 'audio'">
          <b-form-group label="Артист" class="mb-1">
            <v-select
              v-model="newAudioArtist"
              :options="artistOptions"
              :filterable="false"
              label="title"
              placeholder="Поиск артиста"
              @search="onArtistSearch"
              @input="onNewAudioArtistChanged"
            />
          </b-form-group>

          <div v-if="newAudioArtist">
            <b-spinner v-if="newAudioLoadingTracks" small />
            <b-list-group v-else class="track-list mb-1">
              <b-list-group-item
                v-for="t in newAudioTracks"
                :key="t.id"
                :active="newAudioTrack && newAudioTrack.id === t.id"
                class="cursor-pointer"
                @click="newAudioTrack = t"
              >
                <div class="d-flex justify-content-between">
                  <span><strong>{{ t.artist }}</strong> — {{ t.track }}</span>
                  <small class="text-muted">{{ t.play_count || 0 }} прослушиваний</small>
                </div>
              </b-list-group-item>
            </b-list-group>

            <strip-audio-cutter
              v-if="newAudioTrack"
              v-model="newAudioCut"
              :audio-url="newAudioTrack.url"
              :default-start="55"
              :default-duration="25"
            />
          </div>
        </div>

        <b-button
          size="sm"
          variant="primary"
          class="mt-1"
          :disabled="savingNewPart || !canSubmitAddPart"
          @click="submitAddPart"
        >
          <b-spinner v-if="savingNewPart" small /> Добавить
        </b-button>
      </div>
    </div>

    <!-- Cover replacement panel (for image questions) -->
    <div v-if="hasImageParts" class="mt-1">
      <b-button
        v-if="!coverPickerOpen"
        size="sm"
        variant="outline-primary"
        :disabled="!questionArtistId"
        @click="openCoverPicker"
      >
        <feather-icon icon="ImageIcon" /> Заменить обложки
      </b-button>
      <div v-else class="border rounded p-1 mt-1">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong>Выбор обложек</strong>
          <b-button size="sm" variant="link" @click="closeCoverPicker">Отмена</b-button>
        </div>
        <b-spinner v-if="loadingCovers" small />
        <div v-else class="covers-grid">
          <div
            v-for="c in coverOptions"
            :key="c.id"
            class="cover-option"
            :class="{ selected: selectedCoverIds.includes(c.id) }"
            @click="toggleCover(c.id)"
          >
            <img :src="c.url" alt="" />
            <div class="cover-label">{{ c.name || '—' }}</div>
          </div>
        </div>
        <b-button
          size="sm"
          variant="primary"
          :disabled="!selectedCoverIds.length || savingCovers"
          class="mt-1"
          @click="submitCovers"
        >
          <b-spinner v-if="savingCovers" small /> Применить ({{ selectedCoverIds.length }})
        </b-button>
      </div>
    </div>

    <!-- Stats -->
    <div class="stats-block mt-1 mb-1 p-1 border rounded">
      <h6 class="mb-1 d-flex align-items-center" style="gap: 6px">
        <feather-icon icon="TagIcon" /> Статистики
      </h6>
      <div class="d-flex flex-column" style="gap: 6px">
        <div v-for="kind in statKinds" :key="kind.key" class="d-flex align-items-center flex-wrap" style="gap: 4px">
          <small class="stat-label mr-1">{{ kind.label }}:</small>
          <b-badge
            v-for="item in (question[kind.key] || [])"
            :key="kind.key + '-' + item.id"
            :variant="kind.variant"
            class="d-inline-flex align-items-center"
            style="gap: 4px"
          >
            {{ item.name }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="cursor-pointer"
              @click="detachStat(kind.key, item.id)"
            />
          </b-badge>
          <v-select
            :options="availableLookup(kind.key)"
            label="name"
            :reduce="o => o.id"
            :clearable="false"
            placeholder="+ добавить"
            class="stat-add"
            @input="id => attachStat(kind.key, id)"
          />
        </div>
      </div>
    </div>

    <!-- Answer (highlighted, always at bottom) -->
    <div class="answer-block mt-2">
      <b-form-group label="Ответ" class="mb-0">
        <b-input-group>
          <b-form-input v-model="localAnswer" placeholder="Ответ" class="answer-input" />
          <b-input-group-append>
            <b-button variant="success" :disabled="savingAnswer || localAnswer === question.answer" @click="saveAnswer">
              <b-spinner v-if="savingAnswer" small /> Сохранить
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BBadge, BFormGroup, BFormInput, BFormTextarea, BInputGroup,
  BInputGroupAppend, BSpinner, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import {
  ref, reactive, computed, watch, set, del,
} from '@vue/composition-api'
import store from '@/store'
import StripAudioCutter from './builder/StripAudioCutter.vue'

const TYPE_LABELS = {
  audio: 'Аудио', image: 'Картинка', video: 'Видео', text: 'Текст',
}
const TYPE_VARIANTS = {
  audio: 'light-primary', image: 'light-success', video: 'light-warning', text: 'light-secondary',
}

export default {
  components: {
    BCard, BButton, BBadge, BFormGroup, BFormInput, BFormTextarea, BInputGroup,
    BInputGroupAppend, BSpinner, BListGroup, BListGroupItem,
    vSelect, draggable, StripAudioCutter,
  },
  props: {
    stripId: { type: [String, Number], required: true },
    question: { type: Object, required: true },
    lookups: { type: Object, default: () => ({ genres: [], decades: [], origins: [] }) },
  },
  setup(props, { emit }) {
    const localAnswer = ref(props.question.answer || '')
    const savingAnswer = ref(false)
    const savingPartId = ref(null)
    const deletingPartId = ref(null)
    const localParts = ref([...(props.question.parts || [])])

    const addPartMode = ref(null) // 'text' | 'hint' | 'audio' | null
    const newPartText = ref('')
    const newAudioArtist = ref(null)
    const newAudioTracks = ref([])
    const newAudioLoadingTracks = ref(false)
    const newAudioTrack = ref(null)
    const newAudioCut = ref({ startSeconds: 55, durationSeconds: 25 })
    const savingNewPart = ref(false)

    const textBuffers = reactive({})
    const audioReplaceActive = reactive({})
    const audioReplaceState = reactive({})

    const artistOptions = ref([])
    const coverPickerOpen = ref(false)
    const loadingCovers = ref(false)
    const coverOptions = ref([])
    const selectedCoverIds = ref([])
    const savingCovers = ref(false)

    const hasImageParts = computed(() => localParts.value.some(p => p.type === 'image'))
    const questionArtistId = computed(() => {
      const artists = props.question.artists || []
      return artists.length ? artists[0].id : null
    })

    const canSubmitAddPart = computed(() => {
      if (addPartMode.value === 'text' || addPartMode.value === 'hint') {
        return newPartText.value.trim() !== ''
      }
      if (addPartMode.value === 'audio') {
        return Boolean(newAudioTrack.value)
      }
      return false
    })

    const contentParts = computed({
      get: () => localParts.value.filter(p => !p.is_hint),
      set: newList => {
        // сохраняем подсказки (их порядок остаётся как был, висят в конце)
        const hints = localParts.value.filter(p => p.is_hint)
        localParts.value = [...newList, ...hints]
      },
    })
    const hintParts = computed(() => localParts.value.filter(p => p.is_hint))

    // Initialize text buffers (Vue 2 нужен set для новых ключей)
    localParts.value.forEach(p => {
      if (p.type === 'text') set(textBuffers, p.id, p.text || '')
    })

    watch(() => props.question, q => {
      localAnswer.value = q.answer || ''
      localParts.value = [...(q.parts || [])]
      localParts.value.forEach(p => {
        if (p.type === 'text' && textBuffers[p.id] === undefined) {
          set(textBuffers, p.id, p.text || '')
        }
      })
    }, { deep: true })

    const typeLabel = t => TYPE_LABELS[t] || t
    const typeVariant = t => TYPE_VARIANTS[t] || 'light-secondary'
    const isLastContentIdx = idx => idx === contentParts.value.length - 1

    const statKinds = [
      { key: 'genres', label: 'Жанры', variant: 'light-info' },
      { key: 'decades', label: 'Декады', variant: 'light-warning' },
      { key: 'origins', label: 'Происхождение', variant: 'light-success' },
    ]

    const availableLookup = kind => {
      const all = props.lookups?.[kind] || []
      const taken = (props.question[kind] || []).map(i => i.id)
      return all.filter(item => !taken.includes(item.id))
    }

    const attachStat = (kind, id) => {
      if (!id) return
      const apiKind = kind === 'genres' ? 'genre' : kind === 'decades' ? 'decade' : 'origin'
      store.dispatch('strip-builder/attachStat', {
        stripId: props.stripId,
        questionId: props.question.id,
        kind: apiKind,
        id,
      }).then(() => emit('updated'))
    }

    const detachStat = (kind, id) => {
      const apiKind = kind === 'genres' ? 'genre' : kind === 'decades' ? 'decade' : 'origin'
      store.dispatch('strip-builder/detachStat', {
        stripId: props.stripId,
        questionId: props.question.id,
        kind: apiKind,
        id,
      }).then(() => emit('updated'))
    }

    const saveAnswer = () => {
      savingAnswer.value = true
      store.dispatch('strip-builder/updateQuestion', {
        stripId: props.stripId,
        questionId: props.question.id,
        payload: { answer: localAnswer.value },
      })
        .then(() => emit('updated'))
        .finally(() => { savingAnswer.value = false })
    }

    const saveTextPart = part => {
      savingPartId.value = part.id
      store.dispatch('strip-builder/updatePart', {
        stripId: props.stripId,
        questionId: props.question.id,
        partId: part.id,
        payload: { text: textBuffers[part.id] },
      })
        .then(() => emit('updated'))
        .finally(() => { savingPartId.value = null })
    }

    const deletePart = part => {
      if (!window.confirm(`Удалить часть #${part.order} (${part.type}${part.is_hint ? ', подсказка' : ''})?`)) return
      deletingPartId.value = part.id
      store.dispatch('strip-builder/deletePart', {
        stripId: props.stripId,
        questionId: props.question.id,
        partId: part.id,
      })
        .then(() => emit('updated'))
        .finally(() => { deletingPartId.value = null })
    }

    const openAddPart = type => {
      addPartMode.value = type
      newPartText.value = ''
      newAudioArtist.value = (props.question.artists || [])[0] || null
      newAudioTracks.value = []
      newAudioTrack.value = null
      newAudioCut.value = { startSeconds: 55, durationSeconds: 25 }
      if (type === 'audio' && newAudioArtist.value) loadNewAudioTracks(newAudioArtist.value.id)
    }

    const closeAddPart = () => {
      addPartMode.value = null
    }

    const loadNewAudioTracks = artistId => {
      newAudioLoadingTracks.value = true
      newAudioTracks.value = []
      store.dispatch('strip-builder/fetchArtistTracks', { artistId })
        .then(r => { newAudioTracks.value = r.data?.data || r.data || [] })
        .finally(() => { newAudioLoadingTracks.value = false })
    }

    const onNewAudioArtistChanged = () => {
      newAudioTracks.value = []
      newAudioTrack.value = null
      if (newAudioArtist.value) loadNewAudioTracks(newAudioArtist.value.id)
    }

    const submitAddPart = () => {
      const payload = { type: addPartMode.value }
      if (addPartMode.value === 'text' || addPartMode.value === 'hint') {
        if (!newPartText.value.trim()) return
        payload.type = 'text'
        payload.text = newPartText.value
        payload.is_hint = addPartMode.value === 'hint'
      } else if (addPartMode.value === 'audio') {
        if (!newAudioTrack.value) return
        payload.track_id = newAudioTrack.value.id
        payload.start_seconds = newAudioCut.value.startSeconds
        payload.duration_seconds = newAudioCut.value.durationSeconds
      }
      savingNewPart.value = true
      store.dispatch('strip-builder/addPart', {
        stripId: props.stripId,
        questionId: props.question.id,
        payload,
      })
        .then(() => {
          closeAddPart()
          emit('updated')
        })
        .finally(() => { savingNewPart.value = false })
    }

    const onPartsReorder = () => {
      const order = localParts.value.map(p => p.id)
      store.dispatch('strip-builder/reorderParts', {
        stripId: props.stripId,
        questionId: props.question.id,
        order,
      })
        .then(() => emit('updated'))
        .catch(() => emit('updated'))
    }

    const onArtistSearch = (search, loading) => {
      if (!search || search.length < 2) return
      loading(true)
      store.dispatch('strip-builder/searchArtists', { q: search })
        .then(r => { artistOptions.value = r.data?.data || r.data || [] })
        .finally(() => loading(false))
    }

    const openAudioReplace = part => {
      const presetArtist = (props.question.artists || [])[0] || null
      set(audioReplaceActive, part.id, true)
      set(audioReplaceState, part.id, {
        artist: presetArtist,
        loadingTracks: false,
        tracks: [],
        track: null,
        cut: { startSeconds: 55, durationSeconds: 25 },
      })
      if (presetArtist) loadTracksForReplace(part, presetArtist.id)
    }

    const closeAudioReplace = part => {
      set(audioReplaceActive, part.id, false)
      del(audioReplaceState, part.id)
    }

    const loadTracksForReplace = (part, artistId) => {
      const state = audioReplaceState[part.id]
      if (!state) return
      state.loadingTracks = true
      state.tracks = []
      store.dispatch('strip-builder/fetchArtistTracks', { artistId })
        .then(r => { state.tracks = r.data?.data || r.data || [] })
        .finally(() => { state.loadingTracks = false })
    }

    const onArtistChanged = part => {
      const state = audioReplaceState[part.id]
      state.tracks = []
      state.track = null
      if (state.artist) loadTracksForReplace(part, state.artist.id)
    }

    const selectReplacementTrack = (part, track) => {
      audioReplaceState[part.id].track = track
      if (track && track.title) {
        localAnswer.value = track.title
      }
    }

    const canSubmitReplace = part => {
      const s = audioReplaceState[part.id]
      return s && s.track && s.cut && s.cut.durationSeconds > 0
    }

    const submitAudioReplace = part => {
      const s = audioReplaceState[part.id]
      savingPartId.value = part.id
      const payload = {
        track_id: s.track.id,
        start_seconds: s.cut.startSeconds,
        duration_seconds: s.cut.durationSeconds,
      }
      if (s.track.title) payload.answer = s.track.title
      store.dispatch('strip-builder/replaceAudioPart', {
        stripId: props.stripId,
        questionId: props.question.id,
        partId: part.id,
        payload,
      })
        .then(() => {
          closeAudioReplace(part)
          emit('updated')
        })
        .finally(() => { savingPartId.value = null })
    }

    const openCoverPicker = () => {
      if (!questionArtistId.value) return
      coverPickerOpen.value = true
      loadingCovers.value = true
      selectedCoverIds.value = []
      store.dispatch('strip-builder/fetchArtistCovers', { artistId: questionArtistId.value })
        .then(r => { coverOptions.value = r.data?.data || r.data || [] })
        .finally(() => { loadingCovers.value = false })
    }

    const closeCoverPicker = () => {
      coverPickerOpen.value = false
      coverOptions.value = []
      selectedCoverIds.value = []
    }

    const toggleCover = id => {
      const idx = selectedCoverIds.value.indexOf(id)
      if (idx >= 0) selectedCoverIds.value.splice(idx, 1)
      else selectedCoverIds.value.push(id)
    }

    const submitCovers = () => {
      savingCovers.value = true
      store.dispatch('strip-builder/replaceCovers', {
        stripId: props.stripId,
        questionId: props.question.id,
        coverIds: selectedCoverIds.value,
      })
        .then(() => {
          closeCoverPicker()
          emit('updated')
        })
        .finally(() => { savingCovers.value = false })
    }

    return {
      localAnswer, savingAnswer, savingPartId, deletingPartId,
      localParts, contentParts, hintParts, textBuffers, typeLabel, typeVariant, isLastContentIdx,
      audioReplaceActive, audioReplaceState, artistOptions,
      hasImageParts, questionArtistId,
      coverPickerOpen, loadingCovers, coverOptions, selectedCoverIds, savingCovers,
      addPartMode, newPartText, newAudioArtist, newAudioTracks, newAudioLoadingTracks,
      newAudioTrack, newAudioCut, savingNewPart, canSubmitAddPart,
      saveAnswer, saveTextPart, deletePart,
      statKinds, availableLookup, attachStat, detachStat,
      openAddPart, closeAddPart, onNewAudioArtistChanged, submitAddPart,
      onPartsReorder,
      openAudioReplace, closeAudioReplace, onArtistSearch, onArtistChanged,
      selectReplacementTrack, canSubmitReplace, submitAudioReplace,
      openCoverPicker, closeCoverPicker, toggleCover, submitCovers,
    }
  },
}
</script>

<style scoped>
.part-row {
  align-items: stretch;
}
.part-row.has-hints .part-card {
  flex: 0 0 calc(83.333% - 4px);
  max-width: calc(83.333% - 4px);
}
.hint-slot {
  flex: 0 0 calc(16.667% - 4px);
  max-width: calc(16.667% - 4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.part-card {
  background: #f8f8fb;
}
.part-drag-handle {
  cursor: move;
}
.part-thumb {
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
}
.track-list {
  max-height: 280px;
  overflow-y: auto;
}
.covers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}
.cover-option {
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  text-align: center;
  background: #fff;
}
.cover-option img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
}
.cover-option.selected {
  border-color: #7367f0;
}
.cover-label {
  font-size: 0.75rem;
  margin-top: 4px;
  color: #6e6b7b;
  word-break: break-word;
}
.answer-block {
  background: linear-gradient(90deg, #fff3cd 0%, #ffeeba 100%);
  border-left: 4px solid #ff9f43;
  border-radius: 4px;
  padding: 12px 14px;
  box-shadow: 0 2px 6px rgba(255, 159, 67, 0.15);
}
.answer-input {
  font-size: 1.05rem;
  font-weight: 600;
  background: #fff;
}
.hints-row {
  padding: 4px 8px;
  background: #f0f6ff;
  border-radius: 4px;
  border-left: 3px solid #7367f0;
}
.hint-item {
  width: 120px;
}
.hint-input {
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
}
.add-part-form {
  background: #fafbfd;
}
.stats-block {
  background: #fafbfd;
}
.stat-label {
  min-width: 100px;
  font-weight: 600;
  color: #5e5873;
}
.stat-add {
  min-width: 180px;
}
.stat-add :deep(.vs__dropdown-toggle) {
  min-height: 28px;
  padding: 0 4px;
}
</style>
